import React from 'react';
import { useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  ModalIsland,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { msgs } from '../../utilities/messages';

import DownloadKeysModalContent from './components/DownloadKeysModalContent';
import useDownloadKeyForm from './hooks/useDownloadKeyForm';
import { parseKeyConfig } from './helpers';

import './DownloadKeysModal.css';

function DownloadKeysModal({
  isOpen,
  onClose,
  onSubmit,
  setAlertBannerData,
  nextKeySlot,
  sshKeyExpiryDurationInHours,
  sshKeyPassphraseMandatory,
  sshKeyPassphraseMinLength,
  sshKeyPassphraseMaxLength,
}) {
  const localize = useLocalize();
  const config = parseKeyConfig({
    sshKeyExpiryDurationInHours,
    sshKeyPassphraseMandatory,
    sshKeyPassphraseMinLength,
    sshKeyPassphraseMaxLength,
  });

  const form = useDownloadKeyForm({
    keyValidityHours: config.keyValidityHours,
    isPassphraseMandatory: config.isPassphraseMandatory,
    minPassphraseLength: config.minPassphraseLength,
    maxPassphraseLength: config.maxPassphraseLength,
    nextKeySlot,
    onSubmit,
    onClose,
    setAlertBannerData,
    localize,
  });

  const footerContent = (
    <FooterActionBar className='DownloadKeysModal-footer'>
      <Box className='DownloadKeysModal-footerButtons'>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={form.handleCancel}>
          {localize(msgs.pam.common.cancel)}
        </Button>
        <Button
          type='button'
          kind={form.canSubmit ? 'filled' : 'outlined'}
          size='medium'
          isLoading={form.isSubmitting}
          onClick={form.handleSubmit}>
          {localize(msgs.pam.modalPage.page.submitBtn)}
        </Button>
      </Box>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      className='DownloadKeysModal'
      isOpen={isOpen}
      onClose={form.handleCancel}
      title={localize(msgs.pam.apiKeys.download.title)}
      subtitle={localize(msgs.pam.apiKeys.download.subtitle)}
      contentBody={<DownloadKeysModalContent form={form} localize={localize} />}
      FooterActionBarComp={footerContent}
    />
  );
}

DownloadKeysModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setAlertBannerData: PropTypes.func.isRequired,
  nextKeySlot: PropTypes.number.isRequired,
  sshKeyExpiryDurationInHours: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  sshKeyPassphraseMandatory: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
  sshKeyPassphraseMinLength: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  sshKeyPassphraseMaxLength: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

DownloadKeysModal.defaultProps = {
  sshKeyExpiryDurationInHours: undefined,
  sshKeyPassphraseMandatory: undefined,
  sshKeyPassphraseMinLength: undefined,
  sshKeyPassphraseMaxLength: undefined,
};

export default DownloadKeysModal;
